import { EOrderType } from './../../enums/EOrderType';
import { ESortRule } from './../../enums/ESortRule';
import { IBaseProps } from './../../types/data';

export interface IOYOrderBarProps extends IBaseProps {
  /**
   * 排序数据
   * @description 你可以传递完整的数据对象.也可以根据key值快捷传递
   */
  data?: (TOYOrderBarData | string | any[])[];
  /**
   * 排序规则
   */
  rules?: string[][];
  /**
   * publishPrice
   * @params 价格输入框
   */
  publishPrice?: string;
  /** 是否渲染右侧搜索框 */
  search?: boolean;
  /**
   * 价格搜索事件
   * @param value
   */
  onPublishPriceChange?(value: string): void;
  /**
   * 搜索框搜索事件
   * @param value
   */
  onSearch?(value: string): void;
  /**
   * 排序选项变化回调
   * @param data 变化后的排序数据
   */
  onChange?(data: ({ sortRule: string; sortType: any; } | null)[]): void;
}

export interface IOYOrderBarState {
  data: TOYOrderBarData[]
  publishPrice: string[]
}

export interface ITOYOrderBarSelectData {
  type: EOrderType.Select,
  _name: string;
  name: string;
  info: IOYOrderBarInfo[]
}

export interface IOYOrderBarInfo {
  name: string;
  sortType: string;
  sortRule: ESortRule;
  value: any;
}

export interface ITOYOrderBarPublishPriceData {
  name: string;
  type: EOrderType.PublishPrice;
  value: string;
}

export interface IOYOrderBarData {
  name: string;
  sortType: string;
  sortRule: ESortRule;
  defaultRule?: ESortRule;
  type: EOrderType;
}

export type TOYOrderBarData = IOYOrderBarData | ITOYOrderBarSelectData | ITOYOrderBarPublishPriceData;

export const defaultSortData: TOYOrderBarData[] = [
  {
    "sortType": "default",
    "sortRule": ESortRule.Desc,
    "name": "默认",
    "type": EOrderType.None
  },
  {
    "sortType": "isIntentionPayment",
    "sortRule": ESortRule.None,
    "name": "意向金支付",
    "type": 7
  },
  {
    "sortType": "newResource",
    "sortRule": ESortRule.None,
    "name": "新货优先",
    "type": 1
  },
  {
    "sortType": "publishPrice",
    "sortRule": ESortRule.None,
    "name": "价格",
    "type": 3
  },
  {
    "name": '',
    "type": 5,
    "value": ""
  },
  {
    "sortType": "publishDate",
    "sortRule": ESortRule.None,
    "defaultRule": ESortRule.Desc,
    "name": "上架日期",
    "type": 3
  },
  {
    "sortType": "balanceWeight",
    "sortRule": ESortRule.None,
    "name": "重量",
    "type": 3
  },
  {
    "sortType": "spec1",
    "sortRule": ESortRule.None,
    "name": "厚度/直径",
    "type": 3
  },
  {
    "sortType": "spec3",
    "sortRule": ESortRule.None,
    "name": "宽度",
    "type": 3
  },
  {
    "name": "其他排序",
    "_name": '',
    "type": EOrderType.Select,
    "info": [
      {
        "sortType": "manufactureNameFilter",
        "sortRule": ESortRule.None,
        "name": "产地升序",
        "value": "1"
      },
      {
        "sortType": "manufactureNameFilter",
        "sortRule": ESortRule.None,
        "name": "产地降序",
        "value": "0"
      },
      {
        "sortType": "shopSignFilter",
        "sortRule": ESortRule.None,
        "name": "牌号升序",
        "value": "1"
      },
      {
        "sortType": "shopSignFilter",
        "sortRule": ESortRule.None,
        "name": "牌号降序",
        "value": "0"
      },
      {
        "sortType": "prioritizationDateFilter",
        "sortRule": ESortRule.None,
        "name": "立即交货优先",
        "value": "1"
      }
    ]
  }
]

export const sortMap: any = {
  default: {
    "sortRule": ESortRule.Desc,
    "name": "默认",
    "type": EOrderType.None
  },
  isIntentionPayment: {
    "sortRule": ESortRule.None,
    "name": "意向金支付",
    "type": 7
  },
  newResource: {
    "sortRule": ESortRule.None,
    "name": "新货优先",
    "type": 1
  },
  publishPrice: {
    "sortRule": ESortRule.None,
    "defaultRule": ESortRule.Asc,
    "name": "价格",
    "type": 3
  },
  minPrice: {
    "sortRule": ESortRule.None,
    "defaultRule": ESortRule.Asc,
    "name": "价格",
    "type": 3
  },
  publishPriceInput: {
    "name": '',
    "value": "",
    "type": 5
  },
  publishDate: {
    "sortRule": ESortRule.None,
    "defaultRule": ESortRule.Desc,
    "name": "上架日期",
    "type": 3
  },
  maxPublishDate: {
    "sortRule": ESortRule.None,
    "defaultRule": ESortRule.Desc,
    "name": "上架日期",
    "type": 3
  },
  balanceWeight: {
    "sortRule": ESortRule.None,
    "defaultRule": ESortRule.Asc,
    "name": "重量",
    "type": 3
  },
  minUnitWeight: {
    "sortRule": ESortRule.None,
    "defaultRule": ESortRule.Asc,
    "name": "重量",
    "type": 3
  },
  spec1: {
    "sortRule": ESortRule.None,
    "defaultRule": ESortRule.Asc,
    "name": "厚度/直径",
    "type": 3
  },
  spec3: {
    "sortRule": ESortRule.None,
    "defaultRule": ESortRule.Asc,
    "name": "宽度",
    "type": 3
  },
  spec5: {
    "sortRule": ESortRule.None,
    "defaultRule": ESortRule.Asc,
    "name": "长度",
    "type": 3
  },
  manufactureNameFilter: {
    "sortRule": ESortRule.None,
    "name": "产地",
    "type": 3
  },
  shopSignFilter: {
    "sortRule": ESortRule.None,
    "name": "牌号",
    "type": 3
  },
  deliveryMonth: {
    "sortRule": ESortRule.None,
    "name": "交货期",
    "type": 3
  },
  createDate: {
    "sortRule": ESortRule.None,
    "name": "发布日期",
    "type": 3
  },
  prioritizationDateFilter: {
    "sortRule": ESortRule.None,
    "name": "立即交货优先",
    "type": 1
  }
}




// WEBPACK FOOTER //
// ./src/components/OYOrderBar/const.ts